/**
 * Site-wide redirect registry.
 *
 * IMPORTANT — How redirects work on Lovable hosting:
 *
 * 1. **HTTPS, www→non-www, and trailing-slash canonicalization**
 *    These happen at the hosting/edge layer and are **not** controlled from
 *    application code. Lovable's edge already serves the site over HTTPS and
 *    the canonical host `caffecavalli.com` (no `www.`). Files like
 *    `_redirects`, `netlify.toml`, or `vercel.json` are NOT processed by
 *    Lovable hosting, so we do not include them here. The `<link rel="canonical">`
 *    tag in `SEOHead` reinforces the canonical host/path for search engines.
 *
 * 2. **Path redirects (URL changes)**
 *    Because this is a static SPA, true HTTP 301 responses for app routes
 *    can't be issued from the client. Instead we maintain the table below
 *    and apply it inside React Router via `<RedirectsHandler>` (which uses
 *    `<Navigate replace />`, the SPA equivalent of a 301: it rewrites the
 *    URL without leaving an entry in browser history). Search engines that
 *    follow these client-side redirects will resolve to the new canonical URL.
 *
 * 3. **404 status codes**
 *    Pure static SPAs always respond with HTTP 200 + `index.html` for unknown
 *    paths (the SPA fallback). To give Google a real 404 we set
 *    `<meta name="prerender-status-code" content="404">` and `noindex` on the
 *    NotFound page. If true HTTP 404 is required later, the project must
 *    move to SSR or add an edge function — a static SPA cannot return non-200
 *    status codes for unknown paths on its own.
 *
 * To add a redirect: append an entry below. `from` is matched exactly (case
 * sensitive, no trailing slash). `to` is the new canonical path.
 */

export interface Redirect {
  from: string;
  to: string;
  /** ISO date the redirect was added — useful for future cleanup. */
  added: string;
  /** Why the redirect exists. */
  reason: string;
}

export const redirects: Redirect[] = [
  // No path-level URL changes have been made yet.
  // Examples for future use:
  // { from: "/menus", to: "/menu", added: "2026-04-25", reason: "Pluralized URL deprecated" },
  // { from: "/contact", to: "/#contact", added: "2026-04-25", reason: "Contact lives on home page" },
];

/** Normalize a pathname before lookup: strip trailing slash (except root). */
export const canonicalizePath = (pathname: string): string => {
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }
  return pathname;
};

/** Look up a redirect target for a given pathname. Returns null if none. */
export const findRedirect = (pathname: string): string | null => {
  const normalized = canonicalizePath(pathname);
  // Trailing-slash canonicalization itself counts as a redirect.
  if (normalized !== pathname) return normalized;
  const match = redirects.find((r) => r.from === normalized);
  return match ? match.to : null;
};
